import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Menu, MenuItem, Modal, Slide } from '@material-ui/core';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { BiDotsVerticalRounded, BiImage, BiMenu, BiX } from 'react-icons/bi';
import { IoIosWarning } from 'react-icons/io';

import { MdCheckCircle, MdError } from 'react-icons/md';
import Toggle from '../Toggle';
import Button from '../Button';

import {
  TableRow,
  TableCell,
  ProductDetails,
  ActionButton,
  ModalHeader,
  BannerImage,
  ModalBody,
} from './styles';

import PlaceholderBannerImage from '../../assets/no_image.png';
import { IFile } from '../../pages/Configuracoes/TabBar/tabs/Layout/Bannerv2';
import Select from '../Select';
import { useToast } from '../../hooks/toast';
import GroupsService from '../../services/GroupsService';
import { useAuth } from '../../hooks/auth';
import api from '../../services/api';
import FileInput from '../FileInput';

type BannerRowProps = {
  file: IFile;
  processUpload: (uploadedFile: IFile) => void;
  isLoading?: boolean;
  isExpanded?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  onDelete(): void;
  onChangePauseSale(): void;
  setUploadedFiles: React.Dispatch<React.SetStateAction<IFile[]>>;
  onSelectFile: (file: any, id: string) => void
};

const BannerRow: React.FC<BannerRowProps> = ({
  file,
  isLoading = false,
  isExpanded = true,
  dragHandleProps,
  onDelete,
  onChangePauseSale,
  processUpload,
  onSelectFile,
  setUploadedFiles,
}) => {
  const { addToast } = useToast();
  const { user } = useAuth();

  const optionsButtonRef = useRef<HTMLButtonElement>(null);

  const [modalIsOpenDelete, setModalIsOpenDelete] = useState(false);
  const [modalIsOpenEdit, setModalIsOpenEdit] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [produtos, setProdutos] = useState<{
    id: number;
    label: string;
  }[]>([]);
  const [selectedProduto, setSelectedProduto] = useState<{
    id: number;
    label: string;
  } | null>(null);

  const isOpenLinkToCompanyStore = async (): Promise<void> => {
    addToast({
      title: 'Aguarde',
      type: 'info',
    });

    try {
      const { data } = await api.get(`/empresa/${user.empresa_id}`);

      window.open(
        `${process.env.REACT_APP_VENDAS_URL}/${data.slug}?selectedProduct=${selectedProduto?.id}`,
        '_blank',
      );
    } catch (err) {
      addToast({
        title: 'Algo deu errado',
        type: 'error',
        description: (err as any).response?.data?.message || 'Tente novamente.',
      });
    }
  };

  const handleOpenOptions = useCallback(
    () => setIsOptionsOpen(true),
    []
  );

  const handleCloseOptions = useCallback(
    () => setIsOptionsOpen(false),
    []
  );

  const handleAttemptToDeleteProduct = useCallback(() => {
    setModalIsOpenDelete(true);
    handleCloseOptions();
  }, [handleCloseOptions]);

  const handleEditProduct = useCallback(() => {
    setModalIsOpenEdit(true)
    handleCloseOptions();
  }, [handleCloseOptions]);

  const handleDeleteBanner = useCallback(() => {
    handleCloseOptions();
    onDelete();
  }, [onDelete, handleCloseOptions]);

  const handleSubmitProduto = async (): Promise<void> => {
    try {
      await api.put(`/banner/${user.empresa_id}`, {
        banners: [
          {
            ...file,
            product_id: (selectedProduto?.id || null) as any,
          },
        ],
      });

      addToast({
        type: 'success',
        title: 'Tudo certo.',
        description: 'Producto associado com sucesso.',
      });

      setUploadedFiles((oldState) => {
        const newFilesList = [...oldState];

        const foundIndex = newFilesList.findIndex(
          (newFile) => newFile.id === file.id,
        )!;
        const found = newFilesList.find((newFile) => newFile.id === file.id)!;

        newFilesList.splice(foundIndex, 1, {
          ...found,
          product_id: selectedProduto?.id || undefined,
        });

        return newFilesList;
      });
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        type: 'error',
        title: 'Algo deu errado.',
        description: message,
      });
    } finally {
      setSelectedProduto(null);
    }
  };

  const getProductId = useCallback((): void => {
    if (produtos.length > 0 && file.product_id) {
      const findProduct = produtos.find(
        (prod) => Number(prod.id) == Number(file.product_id),
      );

      if (findProduct) {
        setSelectedProduto({ id: findProduct.id, label: findProduct.label });
      }
    }
  }, [file.product_id, produtos]);

  useEffect(() => {
    async function loadProducts(): Promise<void> {
      try {
        const groupsWithProducts = await GroupsService.listGroups(
          user.empresa_id,
          true,
        );

        const products = groupsWithProducts
          .map((group) => {
            if (group.type === 2) {
              return group.products?.map((product) => product);
            }
            return group.products;
          })
          .flat(2);

        setProdutos(
          products.map((produto) => ({
            id: produto!.id,
            label: produto!.name,
          })),
        );
      } catch (err) {
        addToast({
          title: 'Algo deu errado',
          type: 'error',
          description:
            (err as any).response?.data?.message || 'Tente novamente.',
        });
      }
    }

    loadProducts();
  }, [addToast, user.empresa_id]);

  useEffect(() => {
    getProductId();
  }, [file.product_id, getProductId, produtos]);

  return (
    <TableRow>
      <TableCell>
        <ProductDetails>
          <button type="button" disabled={isLoading} {...dragHandleProps}>
            <BiMenu />
          </button>
          <BannerImage
            placeholder={PlaceholderBannerImage}
            url={file.preview}
            onClick={handleEditProduct}
          >
            <div />
          </BannerImage>
          {selectedProduto?.label ? (
            <div
              className="redirect__product"
              onClick={isOpenLinkToCompanyStore}
              data-tooltip="Abrir este produto na sua loja"
            >
              {selectedProduto?.label}
            </div>
          ) : (
            <p>Sem produto relacionado</p>
          )}
        </ProductDetails>
      </TableCell>

      <TableCell>
        {file.progress && <CircularProgress size={24} />}
        {file.uploaded && <MdCheckCircle size={24} color="#35ba83" />}
        {file.error && (
          <MdError
            size={24}
            color="#f64444"
            onClick={() => processUpload(file)}
            style={{ cursor: 'pointer' }}
          />
        )}
      </TableCell>
      <TableCell>
        <Toggle
          checked={file.able}
          firstText="Pausado"
          secondText="Ativado"
          onToggle={onChangePauseSale}
        />
      </TableCell>
      <TableCell>
        <ActionButton
          isExpanded={isExpanded}
          ref={optionsButtonRef}
          type="button"
          onClick={handleOpenOptions}
        >
          <BiDotsVerticalRounded />
        </ActionButton>
        <Menu
          id={`simple-product-${file.id}-options-menu`}
          anchorEl={optionsButtonRef.current}
          keepMounted
          open={isOptionsOpen}
          onClose={handleCloseOptions}
          PaperProps={{
            style: {
              maxHeight: 48 * 4.5,
              width: '20ch',
            },
          }}
        >
          <MenuItem onClick={handleEditProduct}>Editar</MenuItem>
          <MenuItem onClick={handleAttemptToDeleteProduct}>Excluir</MenuItem>
        </Menu>
      </TableCell>

      <Modal
        open={modalIsOpenDelete}
        onClose={() => setModalIsOpenDelete(false)}
        closeAfterTransition
        style={{
          display:' flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Slide
          direction="up"
          in={modalIsOpenDelete}
          mountOnEnter
          unmountOnExit
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              width: 'fit-content',
              padding: '20px',
              height: 'fit-content',
              background: "#fff",
              borderRadius: '4px',
              position: 'relative',
              gap: '8px',
              maxWidth: '350px'
            }}
          >
            <ModalHeader>
              <div>
                <IoIosWarning />
              </div>
              <h1>Atenção</h1>
              <button type="button" onClick={() => setModalIsOpenDelete(false)}>
                <BiX />
              </button>
            </ModalHeader>
            <p>
              Ao realizar a ação o banner &quot;{file.name}
              &quot; será excluídos.
              <br />
              <strong>Deseja prosseguir?</strong>
            </p>
            <Button full onClick={handleDeleteBanner}>
              Confirmar
            </Button>
            <Button
              full
              variant="outlined"
              onClick={() => setModalIsOpenDelete(false)}
            >
              Cancelar
            </Button>
          </Box>
        </Slide>
      </Modal>

      <Modal
        open={modalIsOpenEdit}
        onClose={() => setModalIsOpenEdit(false)}
        closeAfterTransition
        style={{
          display:' flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Slide
          direction="up"
          in={modalIsOpenEdit}
          mountOnEnter
          unmountOnExit
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              width: 'fit-content',
              padding: '20px',
              height: 'fit-content',
              background: "#fff",
              borderRadius: '4px',
              position: 'relative',
              gap: '8px',
              maxWidth: '350px'
            }}
          >
            <ModalHeader>
              <h1>Edição</h1>
              <button
                type="button"
                onClick={() => {
                  getProductId();
                  setModalIsOpenEdit(false)
                }}
              >
                <BiX />
              </button>
            </ModalHeader>
            <ModalBody>
              <FileInput
                id="updateBanner"
                icon={BiImage}
                title="Escolha uma imagem"
                selectedImage={file.preview.split('files/')[1]}
                acceptableTypes=".jpeg,.jpg,.png"
                subtitle="Formatos: .jpeg, .jpg, .png"
                containerStyle={{
                  width: 'auto',
                  maxWidth: 300,
                  marginTop: 20,
                  marginBottom: 20,
                }}
                onRemove={handleAttemptToDeleteProduct}
                onSelectFile={(_file) => {
                  onSelectFile(_file, file.id);
                  setModalIsOpenEdit(false)
                }}
              />

              <div className="form">
                <Select
                  name="produto_relacionado"
                  placeholder="produto relacionado"
                  options={produtos}
                  value={selectedProduto}
                  onChange={(value: any) => setSelectedProduto(value)}
                  isClearable
                />
              </div>
            </ModalBody>
            <Button
              full
              onClick={() => {
                handleSubmitProduto();
                setModalIsOpenEdit(false)
              }}
            >
              Confirmar
            </Button>
            <Button
              full
              variant="outlined"
              onClick={() => {
                getProductId();
                setModalIsOpenEdit(false)
              }}
            >
              Cancelar
            </Button>

          </Box>
        </Slide>
      </Modal>
    </TableRow>
  );
};

export default BannerRow;
